import React, { useState, useRef, useEffect } from "react";
import { useField } from "formik";
import InputField from "@/components/form/InputField";
import { MapPin } from "lucide-react";
import Map, { MapRef, MapMarker } from "@/components/map-view/Map";
import { IAny } from "@/types/breeze/Any";
import { Box, Text } from "@radix-ui/themes";

interface GeoFieldProps {
  name: string;
  label: string;
  description?: string;
  required?: boolean;
  height?: string;
  isEditing?: boolean;
  individualEditAllowed?: boolean;
  onEditSaved?: (name: string, value: string) => void;
}

export default function GeoField({
  name,
  label,
  description,
  required = false,
  height = "300px",
  isEditing = true,
  individualEditAllowed = false,
  onEditSaved,
}: GeoFieldProps) {
  const [field, _, helpers] = useField(name);
  const mapRef = useRef<MapRef>(null);
  const [mapMarker, setMapMarker] = useState<MapMarker | null>(null);
  const [isIndividualEditing, setIsIndividualEditing] = useState(false);

  // Parse coordinates from field value
  const parseCoordinates = (
    value: string,
  ): { lat: number; lng: number } | null => {
    try {
      if (!value) return null;

      // Try to parse as [lat, lon] format
      if (value.startsWith("[") && value.endsWith("]")) {
        const coords = value.slice(1, -1).split(",").map(Number);
        if (coords.length === 2 && !isNaN(coords[0]) && !isNaN(coords[1])) {
          return { lat: coords[0], lng: coords[1] }; // Use coords directly: [lat,lon] -> {lat,lng}
        }
      }

      // Check if it's already in the format we need
      const parts = value.split(",").map((part) => part.trim());
      if (parts.length === 2) {
        const lat = parseFloat(parts[0]);
        const lng = parseFloat(parts[1]);
        if (!isNaN(lat) && !isNaN(lng)) {
          return { lat, lng };
        }
      }

      return null;
    } catch (error) {
      console.error("Error parsing coordinates:", error);
      return null;
    }
  };

  // Format coordinates to string
  const formatCoordinates = (coords: { lat: number; lng: number }): string => {
    return `[${coords.lat.toFixed(6)}, ${coords.lng.toFixed(6)}]`;
  };

  // Handle when a location is selected on the map
  const handleMapClick = (event: any) => {
    if (event.latLng && (isIndividualEditing || isEditing)) {
      const newPosition = {
        lat: event.latLng.lat(),
        lng: event.latLng.lng(),
      };
      const formattedCoords = formatCoordinates(newPosition);
      helpers.setValue(formattedCoords);
    }
  };

  // Update map marker based on field value
  useEffect(() => {
    const coords = parseCoordinates(field.value);
    if (coords) {
      setMapMarker({
        id: "location",
        position: coords,
        isActive: true,
        draggable: isIndividualEditing || isEditing,
        data: {
          id: 1,
          title: "Selected Location",
          description: `${coords.lat.toFixed(6)}, ${coords.lng.toFixed(6)}`,
          locked: false,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
          severity: 0,
        } as IAny,
      });
    } else {
      setMapMarker(null);
    }
  }, [field.value]);

  // Handle refresh button click
  const handleRefresh = () => {
    const coords = parseCoordinates(field.value);
    if (coords && mapRef.current) {
      mapRef.current.centerOnMarker("location");
    }
  };

  // When the input field changes
  const handleInputChange = (value: string) => {
    // If the input field changes, try to update the map
    const coords = parseCoordinates(value);
    if (coords) {
      handleRefresh();
    }
  };

  // Handle marker drag on map
  const handleMarkerDragEnd = (
    markerId: string,
    newPosition: { lat: number; lng: number },
  ) => {
    if (markerId === "location") {
      const formattedCoords = formatCoordinates(newPosition);
      helpers.setValue(formattedCoords);
    }
  };

  return (
    <div className="mb-4">
      <div className="flex flex-col">
        <Box mb={"2"}>
          {label && (
            <Text as="label" htmlFor={name} size="2" weight="medium" mb="1">
              {label} {required && <Text color="red">{" *"}</Text>}
            </Text>
          )}

          {description && (
            <Text className="mb-2 text-sm block" color="gray">
              {description}
            </Text>
          )}
        </Box>

        <div
          className="border rounded-md overflow-hidden mb-2"
          style={{ height: height }}
        >
          <Map
            ref={mapRef}
            markers={mapMarker ? [mapMarker] : []}
            zoom={10}
            center={mapMarker?.position || { lat: 51.505, lng: -0.09 }} // Default to London if no coords
            onMapClick={handleMapClick}
            onMarkerDragEnd={handleMarkerDragEnd}
            className="h-full w-full"
            readOnly={!(isEditing || isIndividualEditing)}
          />
        </div>

        <Text className="mb-1 text-xs" color="gray">
          <MapPin className="inline h-3 w-3 mr-1" />
          Click on the map to set a location, drag the marker to adjust, or
          enter coordinates manually
        </Text>

        <InputField
          type="text"
          name={name}
          description={description}
          required={required}
          options={{
            placeholder:
              "Enter coordinates as [latitude, longitude] or select a location on the map",
          }}
          onChange={handleInputChange}
          isEditing={isEditing}
          individualEditAllowed={individualEditAllowed}
          onEditSaved={onEditSaved}
          onIndividualEditModeChanged={setIsIndividualEditing}
        />
      </div>
    </div>
  );
}
